// ================================================================
// Dynamic Options — گزینه‌های داینامیک برای dropdownها (نسخه‌ی async/cache)
// ================================================================
// چون رندر dropdown همگام است ولی داده‌ها از API می‌آیند،
// از یک کش ماژول‌سطح + lazy-load با خودراه‌اندازی استفاده می‌کنیم:
//  - promptVersions(config) همگام از کش می‌خواند.
//  - اگر کش خالی بود، یک prefetch async شروع می‌کند و پس از پایان،
//    onUpdate() را صدا می‌زند تا UI دوباره رندر شود.

import { TRADES, getVersions } from '../../core/prompts-store.js';
import { getCredentials, MODEL_TO_PROVIDER } from '../../core/credentials-store.js';
import { t } from '../../i18n/index.js';

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
  } catch (e) {
    return iso;
  }
}

// کش نسخه‌ها per-trade. مقدار: { loading, versions } یا undefined (هنوز شروع نشده)
const versionsCache = new Map();
const credentialsCache = new Map();
let onUpdate = null;

// SettingsModal این را ست می‌کند تا بعد از لود، دوباره رندر شود
export function setDynamicOptionsOnUpdate(fn) {
  onUpdate = fn;
}

// پاک‌کردن کش (مثلاً وقتی مودال بسته می‌شود) تا دفعه‌ی بعد تازه لود شود
export function clearPromptVersionsCache() {
  versionsCache.clear();
  credentialsCache.clear();
}

async function prefetch(trade) {
  versionsCache.set(trade, { loading: true, versions: [] });
  try {
    const versions = await getVersions(trade);
    versionsCache.set(trade, { loading: false, versions });
  } catch (e) {
    console.error('prefetch versions failed', e);
    versionsCache.set(trade, { loading: false, versions: [] });
  }
  if (typeof onUpdate === 'function') onUpdate();
}

async function prefetchCredentials(provider) {
  credentialsCache.set(provider, { loading: true, items: [] });
  try {
    const items = await getCredentials(provider);
    credentialsCache.set(provider, { loading: false, items });
  } catch (e) {
    console.error('prefetch credentials failed', e);
    credentialsCache.set(provider, { loading: false, items: [] });
  }
  if (typeof onUpdate === 'function') onUpdate();
}

export const DYNAMIC_OPTIONS = {
  trades: () => TRADES.map(tr => ({ value: tr.key, labelKey: tr.labelKey })),

  promptVersions: (config) => {
    const trade = config.businessType;
    if (!trade) return [];

    const cached = versionsCache.get(trade);

    // هنوز شروع نشده → prefetch را راه بینداز، فعلاً «در حال بارگذاری»
    if (cached === undefined) {
      prefetch(trade);
      return [{ value: '', label: t('nodes.aiAnalysis.promptLoading'), disabled: true }];
    }

    // در حال بارگذاری
    if (cached.loading) {
      return [{ value: '', label: t('nodes.aiAnalysis.promptLoading'), disabled: true }];
    }

    const versions = cached.versions;
    if (versions.length === 0) {
      return [{ value: '', label: t('nodes.aiAnalysis.promptNone'), disabled: true }];
    }

    const opts = [{ value: 'auto', label: '⚡ ' + t('nodes.aiAnalysis.promptAuto') }];
    for (const v of versions) {
      const mark = v.isActive ? '  ●' : '';
      opts.push({ value: v.id, label: fmtDate(v.createdAt) + mark });
    }
    return opts;
  },

  credentials: (config) => {
    const provider = MODEL_TO_PROVIDER[config.model];
    if (!provider) return [];

    const cached = credentialsCache.get(provider);
    if (cached === undefined) {
      prefetchCredentials(provider);
      return [{ value: '', label: t('nodes.aiAnalysis.credLoading'), disabled: true }];
    }
    if (cached.loading) {
      return [{ value: '', label: t('nodes.aiAnalysis.credLoading'), disabled: true }];
    }
    const items = cached.items;
    if (items.length === 0) {
      return [{ value: '', label: t('nodes.aiAnalysis.credNone'), disabled: true }];
    }
    return items.map(c => ({ value: c.id, label: c.label }));
  }
};

export function resolveOptions(field, config) {
  if (field.dynamicOptions && DYNAMIC_OPTIONS[field.dynamicOptions]) {
    return DYNAMIC_OPTIONS[field.dynamicOptions](config || {});
  }
  return field.options || [];
}
